import styled, { keyframes } from 'styled-components';

const slideInFromRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 120px);
  background-color: #f8f9fa;
  position: relative;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  gap: 16px;
  background-color: #f8f9fa;
  
  p {
    color: #666;
    font-size: 16px;
  }
`;

export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  color: white;
  gap: 16px;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 400px;
`;

export const Notification = styled.div`
  background: ${props => {
    switch(props.type) {
      case 'success': return 'linear-gradient(135deg, #28a745, #20c997)';
      case 'warning': return 'linear-gradient(135deg, #ffc107, #fd7e14)';
      default: return 'linear-gradient(135deg, #007bff, #6610f2)';
    }
  }};
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  line-height: 1.4;
  animation: ${slideInFromRight} 0.3s ease-out;
  cursor: pointer;
  font-weight: 500;
  word-break: break-word;
`;

export const NotificationDismiss = styled.div`
  font-size: 11px;
  opacity: 0.8;
  margin-top: 4px;
  text-align: right;
`;

export const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

  h1 {
    margin: 0 0 0.5rem 0;
    font-size: 2.5rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 1.1rem;
    opacity: 0.9;
  }
`;

export const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
`;

export const SectionTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  margin-bottom: 0.5rem;
  color: #495057;
  font-weight: 600;
  font-size: 0.9rem;
`;

// 按钮组还没有可选项时（没选校区 / 校区的星期都排满了）占位的说明
export const ScheduleFieldNote = styled.div`
  min-height: 36px;
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.85rem;
`;

export const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  outline: none;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

export const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  outline: none;
  background-color: white;
  cursor: pointer;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
`;

export const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  background: ${props => {
    switch(props.variant) {
      case 'danger': return 'linear-gradient(135deg, #dc3545, #c82333)';
      case 'success': return 'linear-gradient(135deg, #28a745, #20c997)';
      case 'secondary': return '#6c757d';
      default: return 'linear-gradient(135deg, #007bff, #0056b3)';
    }
  }};
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    background: #f8f9fa;
    color: #495057;
    font-weight: 600;
    padding: 1rem;
    text-align: left;
    border-bottom: 2px solid #dee2e6;
  }

  td {
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
    vertical-align: middle;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

export const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: ${props => props.active ? '#28a745' : '#dc3545'};
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  ${Button} {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;

export const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
`;

export const CampusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border-left: 4px solid #007bff;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  h3 {
    color: #2c3e50;
    font-size: 1.3rem;
    margin: 0;
    font-weight: 600;
  }

  span {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 500;
  }
`;

export const CollapseIcon = styled.span`
  color: #007bff;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  transform: ${props => props.collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  display: inline-block;
  width: 12px;
  text-align: center;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;

  h3 {
    color: #adb5bd;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
  }
`;